import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { FiChevronDown, FiX, FiPlus } from 'react-icons/fi'
import { useLanguageStore } from '../../store/languageStore'
import { soleOptionIdFromList, useAutoSelectSingleOption } from '../../hooks/useAutoSelectSingleOption'
import { isSameEntityId } from '../../utils/entityId'
import DropdownSearchHeader from './DropdownSearchHeader'
import { useLookupRefresh } from '../../hooks/useLookupRefresh'
import { useDropdownFieldConfig } from '../../hooks/useDropdownFieldConfig'
import { formatConfiguredLabel, formatConfiguredParts } from '../../utils/dropdownFieldConfig'
import { mapPagedResponse, useInfiniteLookup } from '../../hooks/useInfiniteLookup'

interface Customer {
  id?: string
  ID?: string
  name?: string
  Name?: string
  nameEn?: string
  contactType?: string
  ContactType?: string
  nbrTax?: string
  NBRTax?: string
  isActive?: boolean
  IsActive?: boolean
  mobile?: string
  phone2?: string
  whatsAppNumber?: string
  patientSerial?: string
}

interface CustomerDropdownProps {
  value?: string | number
  onChange: (customerId: string | undefined, customerName?: string) => void
  placeholder?: string
  className?: string
  error?: string
  allowAddNew?: boolean
  onAddNew?: (searchTerm: string) => void
  disabled?: boolean
  entityMode?: 'customers' | 'patients'
  patientBranchId?: string
  trailingAddon?: ReactNode
  autoSelectSingle?: boolean
  salesRepId?: number
}

function mapCustomerRow(customer: any, isPatients: boolean): Customer | null {
  if (isPatients) {
    const mapped = {
      id: String(customer.id ?? customer.ID ?? ''),
      name: customer.name ?? customer.Name ?? '',
      nameEn: customer.nameEn ?? customer.NameEn ?? customer.name ?? customer.Name ?? '',
      contactType: 'P',
      nbrTax: '',
      mobile: customer.mobile ?? customer.Mobile,
      patientSerial: customer.patientSerial ?? customer.PatientSerial,
      isActive: customer.isActive !== false && customer.IsActive !== false,
    }
    return mapped.isActive !== false && mapped.id && mapped.name ? mapped : null
  }

  const mapped = {
    id: customer.id || customer.ID || '',
    name: customer.name || customer.Name || '',
    nameEn: customer.nameEn || customer.NameEn || customer.name,
    contactType: customer.contactType || customer.ContactType || 'C',
    nbrTax: customer.nbrTax || customer.NBRTax || '',
    mobile: customer.mobile ?? customer.Mobile ?? '',
    phone2: customer.phone2 ?? customer.Phone2 ?? '',
    whatsAppNumber: customer.whatsAppNumber ?? customer.WhatsAppNumber ?? '',
    isActive: customer.isActive !== false && customer.IsActive !== false,
  }
  return mapped.isActive !== false && mapped.id && mapped.name ? mapped : null
}

export default function CustomerDropdown({
  value,
  onChange,
  placeholder,
  className = '',
  error,
  allowAddNew = true,
  onAddNew,
  disabled = false,
  entityMode = 'customers',
  patientBranchId,
  trailingAddon,
  autoSelectSingle = true,
  salesRepId,
}: CustomerDropdownProps) {
  const { language } = useLanguageStore()
  const navigate = useNavigate()
  const isRTL = language === 'ar'
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLDivElement | null)[]>([])
  const handleSelectRef = useRef<(customerId: string) => void>()
  const handleAddNewRef = useRef<() => void>()
  const highlightedIndexRef = useRef(-1)

  const isPatients = entityMode === 'patients'
  const fieldConfig = useDropdownFieldConfig(isPatients ? 'patient' : 'customer')
  const getId = useCallback((c: Customer) => String(c.id || ''), [])

  const {
    items: customers,
    isLoading,
    isFetching,
    isFetchingNextPage,
    refetch,
    sentinelRef,
  } = useInfiniteLookup<Customer>({
    queryKey: isPatients
      ? ['patients', 'dropdown', patientBranchId ?? '']
      : ['customers', 'dropdown', salesRepId ?? ''],
    enabled: isOpen || !value || autoSelectSingle,
    pageSize: 40,
    searchTerm,
    searchFields: fieldConfig.searchFields,
    getId,
    scrollRootRef: listRef,
    fetchPage: async ({ page, pageSize, search, searchFields }) => {
      if (isPatients) {
        const response = await apiClient.get(
          API_ENDPOINTS.patients.list(search || undefined, page, pageSize, patientBranchId)
        )
        return mapPagedResponse(response.data, (row) => mapCustomerRow(row, true), page, pageSize)
      }
      const response = await apiClient.get(API_ENDPOINTS.customers.list, {
        params: {
          page,
          pageSize,
          searchTerm: search || undefined,
          searchFields: searchFields.length > 0 ? searchFields.join(',') : undefined,
          salesRepId: salesRepId || undefined,
        },
      })
      return mapPagedResponse(response.data, (row) => mapCustomerRow(row, false), page, pageSize)
    },
  })

  const { data: selectedById } = useQuery({
    queryKey: [isPatients ? 'patients' : 'customers', 'selected', value],
    enabled: !!value && !customers.some((c) => isSameEntityId(c.id, value)),
    queryFn: async () => {
      const endpoint = isPatients
        ? API_ENDPOINTS.patients.get(String(value))
        : API_ENDPOINTS.customers.get(String(value))
      const response = await apiClient.get(endpoint)
      return mapCustomerRow(response.data, isPatients)
    },
  })

  const displayCustomers = useMemo(() => {
    if (!selectedById?.id) return customers
    if (customers.some((c) => isSameEntityId(c.id, selectedById.id))) return customers
    return [selectedById, ...customers]
  }, [customers, selectedById])

  const selectedCustomer =
    displayCustomers.find((customer: Customer) => isSameEntityId(customer.id, value)) ||
    selectedById ||
    undefined

  const soleCustomerIdStr = soleOptionIdFromList(customers as any, (c: any) => c.id)

  const applySoleCustomer = useCallback(() => {
    const c = customers[0]
    if (!c?.id) return
    onChange(c.id, c.name)
  }, [customers, onChange])

  const { markUserCleared } = useAutoSelectSingleOption({
    soleIdStr: soleCustomerIdStr,
    currentValue: value,
    onApplySole: applySoleCustomer,
    isLoading,
    disabled,
    autoSelectSingle,
    resetKey: `${entityMode}-${patientBranchId ?? ''}`,
  })

  const { onRefresh, isRefreshing } = useLookupRefresh({
    refetch,
    isFetching,
    items: customers as Customer[],
    selectedId: value,
    onClearSelection: () => onChange(undefined),
    language,
  })

  const handleSelect = useCallback((customerId: string) => {
    const customer =
      displayCustomers.find((c) => c.id === customerId) ||
      customers.find((c) => c.id === customerId)
    onChange(customerId, customer?.name)
    setIsOpen(false)
    setSearchTerm('')
    setHighlightedIndex(-1)
  }, [displayCustomers, customers, onChange])

  const handleAddNew = useCallback(() => {
    setIsOpen(false)
    const searchValue = searchTerm.trim()
    if (onAddNew) {
      onAddNew(searchValue)
    } else if (isPatients) {
      navigate('/patients')
    } else {
      navigate('/customers/new', {
        state: {
          initialData: {
            name: searchValue,
            nameEn: searchValue,
          },
        },
      })
    }
    setSearchTerm('')
  }, [searchTerm, onAddNew, navigate, isPatients])

  useEffect(() => {
    handleSelectRef.current = handleSelect
    handleAddNewRef.current = handleAddNew
  }, [handleSelect, handleAddNew])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
        setHighlightedIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    highlightedIndexRef.current = highlightedIndex
  }, [highlightedIndex])

  useEffect(() => {
    if (!isOpen) setHighlightedIndex(-1)
  }, [isOpen])

  useEffect(() => {
    if (isOpen) setHighlightedIndex(-1)
  }, [searchTerm, isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      const itemsList: any[] = []
      if (allowAddNew && searchTerm && displayCustomers.length === 0) {
        itemsList.push({ type: 'add-new' })
      }
      displayCustomers.forEach((customer) => {
        if (customer.id) itemsList.push({ type: 'customer', data: customer })
      })
      const totalItems = itemsList.length

      switch (event.key) {
        case 'ArrowDown':
          if (totalItems === 0) return
          event.preventDefault()
          setHighlightedIndex((prev) => {
            const next = prev < 0 ? 0 : prev < totalItems - 1 ? prev + 1 : prev
            setTimeout(() => {
              itemRefs.current[next]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            }, 0)
            return next
          })
          break
        case 'ArrowUp':
          if (totalItems === 0) return
          event.preventDefault()
          setHighlightedIndex((prev) => {
            const next = prev < 0 ? totalItems - 1 : prev > 0 ? prev - 1 : 0
            setTimeout(() => {
              itemRefs.current[next]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            }, 0)
            return next
          })
          break
        case 'Enter': {
          const currentIndex = highlightedIndexRef.current
          if (currentIndex >= 0 && currentIndex < totalItems) {
            event.preventDefault()
            const item = itemsList[currentIndex]
            if (item.type === 'add-new') handleAddNewRef.current?.()
            else if (item.type === 'customer') handleSelectRef.current?.(item.data.id!)
          }
          break
        }
        case 'Escape':
          event.preventDefault()
          setIsOpen(false)
          setSearchTerm('')
          setHighlightedIndex(-1)
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, displayCustomers, searchTerm, allowAddNew])

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    markUserCleared()
    onChange(undefined)
    setSearchTerm('')
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        dir="ltr"
        onClick={() => {
          if (!disabled) setIsOpen(!isOpen)
        }}
        className={`input-field cursor-pointer flex flex-row items-center gap-2 min-w-0 ${
          error ? 'border-red-500' : ''
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      >
        <span
          dir={isRTL ? 'rtl' : 'ltr'}
          className={`flex-1 min-w-0 truncate ${isRTL ? 'text-right' : 'text-left'} ${!value ? 'text-gray-400' : 'text-gray-800'}`}
        >
          {selectedCustomer
            ? formatConfiguredLabel(selectedCustomer, isPatients ? 'patient' : 'customer', fieldConfig.triggerFields, language)
            : (placeholder ||
                (isPatients
                  ? language === 'ar'
                    ? 'اختر المريض'
                    : 'Select patient'
                  : language === 'ar'
                    ? 'اختر العميل'
                    : 'Select Customer'))}
        </span>
        <div className="flex shrink-0 items-center gap-1">
          {value && !disabled && (
            <button type="button" onClick={handleClear} className="text-gray-400 hover:text-red-500 transition-colors">
              <FiX />
            </button>
          )}
          <FiChevronDown className={`text-gray-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
          {trailingAddon}
        </div>
      </div>

      {error && (
        <p className={`mt-1 text-sm text-red-600 ${isRTL ? 'text-right' : 'text-left'}`}>{error}</p>
      )}

      {isOpen && !disabled && (
        <div
          className={`absolute z-[10000] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-hidden text-gray-900 ${
            isRTL ? 'right-0' : 'left-0'
          }`}
          style={{
            position: 'fixed',
            zIndex: 10000,
            width: dropdownRef.current?.offsetWidth || 'auto',
            top: dropdownRef.current ? dropdownRef.current.getBoundingClientRect().bottom + 4 + 'px' : 'auto',
            left: isRTL ? 'auto' : dropdownRef.current ? dropdownRef.current.getBoundingClientRect().left + 'px' : 'auto',
            right: isRTL
              ? dropdownRef.current
                ? window.innerWidth - dropdownRef.current.getBoundingClientRect().right + 'px'
                : 'auto'
              : 'auto',
          }}
        >
          <DropdownSearchHeader
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onRefresh={onRefresh}
            isRefreshing={isRefreshing}
            isRTL={isRTL}
            placeholder={
              language === 'ar'
                ? 'بحث بالاسم أو الجوال أو الرقم الضريبي… (↑↓ للتنقل)'
                : 'Search by name, mobile, or tax no… (↑↓ to move)'
            }
          />

          <div ref={listRef} className="overflow-y-auto max-h-48">
            {isLoading ? (
              <div className={`p-4 text-center text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                {language === 'ar' ? 'جاري التحميل...' : 'Loading...'}
              </div>
            ) : (
              <>
                {allowAddNew && searchTerm && displayCustomers.length === 0 && (
                  <div
                    ref={(el) => {
                      itemRefs.current[0] = el
                    }}
                    onClick={handleAddNew}
                    onMouseEnter={() => setHighlightedIndex(0)}
                    className={`px-4 py-2 cursor-pointer transition-colors border-b border-gray-200 text-gray-900 ${
                      highlightedIndex === 0 ? 'bg-slate-200' : 'hover:bg-slate-100'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-2 text-primary-700 font-medium">
                      <FiPlus />
                      <span>
                        {isPatients
                          ? language === 'ar'
                            ? `إضافة مريض جديد: ${searchTerm}`
                            : `Add new patient: ${searchTerm}`
                          : language === 'ar'
                            ? `إضافة عميل جديد: ${searchTerm}`
                            : `Add new customer: ${searchTerm}`}
                      </span>
                    </div>
                  </div>
                )}
                {displayCustomers.length === 0 && (!allowAddNew || !searchTerm) ? (
                  <div className={`p-4 text-center text-gray-500 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {searchTerm
                      ? isPatients
                        ? language === 'ar'
                          ? 'لا يوجد مرضى مطابقون للبحث'
                          : 'No patients match your search'
                        : language === 'ar'
                          ? 'لا توجد عملاء مطابقة للبحث'
                          : 'No customers match your search'
                      : isPatients
                        ? language === 'ar'
                          ? 'لا يوجد مرضى'
                          : 'No patients found'
                        : language === 'ar'
                          ? 'لا توجد عملاء'
                          : 'No customers found'}
                  </div>
                ) : (
                  displayCustomers.map((customer: Customer, index: number) => {
                    if (!customer.id) return null
                    const isSelected = isSameEntityId(customer.id, value)
                    const itemIndex = (allowAddNew && searchTerm && displayCustomers.length === 0 ? 1 : 0) + index
                    const isHighlighted = highlightedIndex === itemIndex
                    const parts = formatConfiguredParts(
                      customer,
                      isPatients ? 'patient' : 'customer',
                      fieldConfig.listFields,
                      language
                    )
                    return (
                      <div
                        key={customer.id}
                        ref={(el) => {
                          itemRefs.current[itemIndex] = el
                        }}
                        onClick={() => handleSelect(customer.id!)}
                        onMouseEnter={() => setHighlightedIndex(itemIndex)}
                        className={`px-4 py-2 cursor-pointer transition-colors text-gray-900 ${
                          isHighlighted ? 'bg-slate-200' : 'hover:bg-slate-100'
                        } ${isSelected ? 'font-semibold bg-slate-50' : ''} ${isRTL ? 'text-right' : 'text-left'}`}
                      >
                        <div className="font-medium text-gray-900">{parts.primary}</div>
                        {parts.secondary.length > 0 && (
                          <div className="text-xs text-gray-600">{parts.secondary.join(' · ')}</div>
                        )}
                      </div>
                    )
                  })
                )}
                <div ref={sentinelRef} className="h-6 flex items-center justify-center text-xs text-gray-400">
                  {isFetchingNextPage
                    ? language === 'ar'
                      ? 'جاري تحميل المزيد…'
                      : 'Loading more…'
                    : null}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
